import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import CircularProfileImage from '../components/CircularProfileImage';

const CONTRACTOR_SERVICES = [
  { value: 'builders', label: 'Builders' },
  { value: 'tilers', label: 'Tilers' },
  { value: 'roofers', label: 'Roofers' },
  { value: 'plumbers', label: 'Plumbers' },
  { value: 'electricians', label: 'Electricians' },
  { value: 'architects', label: 'Architects' },
  { value: 'painters', label: 'Painters' },
  { value: 'landscapers', label: 'Landscapers' },
  { value: 'carpenters', label: 'Carpenters' }
];

const ContractorDashboard = ({ currentUserId }) => {
  const navigate = useNavigate();
  const [contractor, setContractor] = useState(null);
  const [portfolios, setPortfolios] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [portfolioForm, setPortfolioForm] = useState({
    projectTitle: '',
    serviceType: '',
    description: '',
    location: '',
    completionDate: '',
    images: []
  });

  useEffect(() => {
    if (currentUserId) {
      fetchContractorData();
    }
  }, [currentUserId]);

  const fetchContractorData = async () => {
    try {
      setLoading(true);
      const contractorRes = await axios.get(`${API}/contractors/user/${currentUserId}`);
      setContractor(contractorRes.data);
      setEditFormData(contractorRes.data);

      // Fetch portfolios and reviews
      const [portfolioRes, subscriptionRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/portfolios/contractor/${contractorRes.data.id}`),
        axios.get(`${API}/contractor-subscriptions/${contractorRes.data.id}`),
        axios.get(`${API}/contractor-reviews/${contractorRes.data.id}`)
      ]);

      setPortfolios(portfolioRes.data);
      setSubscription(subscriptionRes.data);
      setReviews(reviewsRes.data);
    } catch (error) {
      console.error('Error fetching contractor data:', error);
      if (error.response?.status === 404) {
        // User is not a contractor yet
        navigate('/contractor/register');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      await axios.put(`${API}/contractors/${contractor.id}`, {
        businessName: editFormData.businessName,
        contractorName: editFormData.contractorName,
        description: editFormData.description,
        location: editFormData.location,
        areasServed: editFormData.areasServed,
        contactNumber: editFormData.contactNumber,
        whatsappNumber: editFormData.whatsappNumber,
        email: editFormData.email
      });
      toast.success('Profile updated successfully!');
      setContractor(editFormData);
      setIsEditing(false);
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleStartSubscription = async (isTrial = true) => {
    try {
      await axios.post(`${API}/contractor-subscriptions`, {
        contractorId: contractor.id,
        isTrial
      });
      toast.success(isTrial ? 'Free trial activated!' : 'Subscription activated!');
      fetchContractorData();
    } catch (error) {
      toast.error('Failed to activate subscription');
    }
  };

  const handleAddPortfolio = async () => {
    if (!portfolioForm.projectTitle || !portfolioForm.serviceType || !portfolioForm.completionDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API}/portfolios`, {
        contractorId: contractor.id,
        projectTitle: portfolioForm.projectTitle,
        serviceType: portfolioForm.serviceType,
        description: portfolioForm.description,
        location: portfolioForm.location,
        completionDate: new Date(portfolioForm.completionDate).toISOString(),
        images: portfolioForm.images
      });
      toast.success('Portfolio item added!');
      setShowPortfolioModal(false);
      setPortfolioForm({
        projectTitle: '',
        serviceType: '',
        description: '',
        location: '',
        completionDate: '',
        images: []
      });
      fetchContractorData();
    } catch (error) {
      toast.error('Failed to add portfolio item');
    }
  };

  const handleDeletePortfolio = async (portfolioId) => {
    if (!window.confirm('Are you sure you want to delete this portfolio item?')) return;

    try {
      await axios.delete(`${API}/portfolios/${portfolioId}`);
      toast.success('Portfolio item deleted');
      fetchContractorData();
    } catch (error) {
      toast.error('Failed to delete portfolio item');
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPortfolioForm(prev => ({
          ...prev,
          images: [...prev.images, reader.result]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const getServiceLabel = (value) => {
    const service = CONTRACTOR_SERVICES.find(s => s.value === value);
    return service ? service.label : value;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <i key={i} className={`fas fa-star ${i <= rating ? 'text-yellow-400' : 'text-gray-300'}`}></i>
      );
    }
    return stars;
  };

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-emerald-600"></i>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </main>
    );
  }

  if (!contractor) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50">
        <Card className="text-center p-8">
          <i className="fas fa-hard-hat text-6xl text-gray-300 mb-4"></i>
          <h2 className="text-xl font-semibold mb-2">No Contractor Profile Found</h2>
          <p className="text-gray-500 mb-4">You need to register as a contractor first.</p>
          <Button onClick={() => navigate('/contractor/register')} className="bg-emerald-600 hover:bg-emerald-700">
            Register as Contractor
          </Button>
        </Card>
      </main>
    );
  }

  const isSubscriptionActive = subscription?.status === 'active' || subscription?.status === 'trial';

  return (
    <main className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
          <i className="fas fa-hard-hat text-emerald-600 mr-3"></i>
          Contractor Dashboard
        </h1>

        {/* Status Cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          {/* Verification Status */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Verification</p>
                  <p className="font-semibold capitalize">{contractor.verificationStatus}</p>
                </div>
                <div className={`p-3 rounded-full ${
                  contractor.isVerified ? 'bg-blue-100' : 
                  contractor.verificationStatus === 'pending' ? 'bg-yellow-100' : 'bg-red-100'
                }`}>
                  <i className={`fas ${
                    contractor.isVerified ? 'fa-check-circle text-blue-600' : 
                    contractor.verificationStatus === 'pending' ? 'fa-clock text-yellow-600' : 'fa-times-circle text-red-600'
                  } text-xl`}></i>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Subscription Status */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Subscription</p>
                  <p className="font-semibold capitalize">{subscription?.status || 'Inactive'}</p>
                </div>
                <div className={`p-3 rounded-full ${isSubscriptionActive ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                  <i className={`fas fa-crown ${isSubscriptionActive ? 'text-emerald-600' : 'text-gray-400'} text-xl`}></i>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Rating */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Rating</p>
                  <p className="font-semibold">{contractor.averageRating?.toFixed(1) || '0.0'} / 5.0</p>
                </div>
                <div className="p-3 rounded-full bg-yellow-100">
                  <i className="fas fa-star text-yellow-500 text-xl"></i>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Portfolio Items */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Portfolio Items</p>
                  <p className="font-semibold">{portfolios.length}</p>
                </div>
                <div className="p-3 rounded-full bg-purple-100">
                  <i className="fas fa-images text-purple-600 text-xl"></i>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alerts */}
        {!isSubscriptionActive && (
          <Card className="mb-6 border-yellow-300 bg-yellow-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <i className="fas fa-exclamation-triangle text-yellow-600 text-2xl"></i>
                <div className="flex-1">
                  <h3 className="font-semibold text-yellow-800">Subscription Required</h3>
                  <p className="text-sm text-yellow-700">
                    Your profile is hidden from searches. Activate your subscription to be visible to potential clients.
                  </p>
                </div>
                <Button onClick={() => handleStartSubscription(true)} className="bg-yellow-600 hover:bg-yellow-700">
                  Start Free Trial
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {contractor.verificationStatus === 'pending' && (
          <Card className="mb-6 border-blue-300 bg-blue-50">
            <CardContent className="p-4 flex items-center gap-4">
              <i className="fas fa-clock text-blue-600 text-2xl"></i>
              <div>
                <h3 className="font-semibold text-blue-800">Verification Pending</h3>
                <p className="text-sm text-blue-700">
                  Your documents are being reviewed. This usually takes 1-2 business days.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {contractor.verificationStatus === 'rejected' && (
          <Card className="mb-6 border-red-300 bg-red-50">
            <CardContent className="p-4 flex items-center gap-4">
              <i className="fas fa-times-circle text-red-600 text-2xl"></i>
              <div>
                <h3 className="font-semibold text-red-800">Verification Rejected</h3>
                <p className="text-sm text-red-700">
                  {contractor.verificationNotes || 'Please contact support for more information.'}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Tabs */}
        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="w-full grid grid-cols-4 mb-6">
            <TabsTrigger value="profile" data-testid="tab-profile">
              <i className="fas fa-user mr-2"></i> Profile
            </TabsTrigger>
            <TabsTrigger value="portfolio" data-testid="tab-portfolio">
              <i className="fas fa-images mr-2"></i> Portfolio
            </TabsTrigger>
            <TabsTrigger value="subscription" data-testid="tab-subscription">
              <i className="fas fa-crown mr-2"></i> Subscription
            </TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">
              <i className="fas fa-star mr-2"></i> Reviews
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Business Profile</CardTitle>
                  <CardDescription>Manage your business information</CardDescription>
                </div>
                {!isEditing && (
                  <Button variant="outline" onClick={() => setIsEditing(true)} data-testid="edit-profile-btn">
                    <i className="fas fa-edit mr-2"></i> Edit
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {/* Profile Image Section */}
                <div className="flex flex-col sm:flex-row items-center gap-6 mb-6 pb-6 border-b">
                  <CircularProfileImage
                    src={contractor.profileImage}
                    alt={contractor.businessName}
                    size="xl"
                    editable={true}
                    onImageChange={async (imageData) => {
                      try {
                        // Upload to backend
                        await axios.put(`${API}/contractors/${contractor.id}`, {
                          ...contractor,
                          profileImage: imageData.dataUrl
                        });
                        setContractor({ ...contractor, profileImage: imageData.dataUrl });
                        setEditFormData({ ...editFormData, profileImage: imageData.dataUrl });
                      } catch (error) {
                        toast.error('Failed to update profile picture');
                        throw error;
                      }
                    }}
                  />
                  <div className="text-center sm:text-left">
                    <h3 className="text-xl font-semibold text-gray-900">{contractor.businessName}</h3>
                    <p className="text-gray-600">{contractor.contractorName}</p>
                    <div className="flex items-center gap-2 mt-2 justify-center sm:justify-start">
                      {contractor.isVerified && (
                        <Badge className="bg-blue-100 text-blue-700">
                          <i className="fas fa-check-circle mr-1"></i> Verified
                        </Badge>
                      )}
                      <Badge variant="outline">{contractor.services?.length || 0} Services</Badge>
                    </div>
                  </div>
                </div>

                {isEditing ? (
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Business Name</Label>
                        <Input
                          value={editFormData.businessName || ''}
                          onChange={(e) => setEditFormData({ ...editFormData, businessName: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Contractor Name</Label>
                        <Input
                          value={editFormData.contractorName || ''}
                          onChange={(e) => setEditFormData({ ...editFormData, contractorName: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={editFormData.description || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                        rows={3}
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Location</Label>
                        <Input
                          value={editFormData.location || ''}
                          onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input
                          value={editFormData.email || ''}
                          onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Contact Number</Label>
                        <Input
                          value={editFormData.contactNumber || ''}
                          onChange={(e) => setEditFormData({ ...editFormData, contactNumber: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>WhatsApp Number</Label>
                        <Input
                          value={editFormData.whatsappNumber || ''}
                          onChange={(e) => setEditFormData({ ...editFormData, whatsappNumber: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleUpdateProfile} className="bg-emerald-600 hover:bg-emerald-700">
                        Save Changes
                      </Button>
                      <Button variant="outline" onClick={() => { setIsEditing(false); setEditFormData(contractor); }}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Business Name</p>
                        <p className="font-medium">{contractor.businessName}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Contractor Name</p>
                        <p className="font-medium">{contractor.contractorName}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Services</p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {contractor.services?.map(s => (
                          <Badge key={s} variant="outline">{getServiceLabel(s)}</Badge>
                        ))}
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Location</p>
                        <p className="font-medium">{contractor.location}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Areas Served</p>
                        <p className="font-medium">{contractor.areasServed?.join(', ') || 'N/A'}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Description</p>
                      <p className="font-medium">{contractor.description || 'No description'}</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Contact</p>
                        <p className="font-medium">{contractor.contactNumber}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">WhatsApp</p>
                        <p className="font-medium">{contractor.whatsappNumber}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="font-medium">{contractor.email}</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Portfolio Tab */}
          <TabsContent value="portfolio">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Completed Work Portfolio</CardTitle>
                  <CardDescription>Showcase your best projects to attract clients</CardDescription>
                </div>
                <Dialog open={showPortfolioModal} onOpenChange={setShowPortfolioModal}>
                  <DialogTrigger asChild>
                    <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-portfolio-btn">
                      <i className="fas fa-plus mr-2"></i> Add Project
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Add Portfolio Project</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Project Title *</Label>
                        <Input
                          value={portfolioForm.projectTitle}
                          onChange={(e) => setPortfolioForm({ ...portfolioForm, projectTitle: e.target.value })}
                          placeholder="Kitchen Renovation"
                          data-testid="portfolio-title-input"
                        />
                      </div>
                      <div>
                        <Label>Service Type *</Label>
                        <select
                          value={portfolioForm.serviceType}
                          onChange={(e) => setPortfolioForm({ ...portfolioForm, serviceType: e.target.value })}
                          className="w-full border rounded-md p-2"
                          data-testid="portfolio-service-select"
                        >
                          <option value="">Select service</option>
                          {CONTRACTOR_SERVICES.map(s => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label>Location</Label>
                        <Input
                          value={portfolioForm.location}
                          onChange={(e) => setPortfolioForm({ ...portfolioForm, location: e.target.value })}
                          placeholder="Sandton, Johannesburg"
                        />
                      </div>
                      <div>
                        <Label>Completion Date *</Label>
                        <Input
                          type="date"
                          value={portfolioForm.completionDate}
                          onChange={(e) => setPortfolioForm({ ...portfolioForm, completionDate: e.target.value })}
                          data-testid="portfolio-date-input"
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          value={portfolioForm.description}
                          onChange={(e) => setPortfolioForm({ ...portfolioForm, description: e.target.value })}
                          placeholder="Describe the project..."
                          rows={3}
                        />
                      </div>
                      <div>
                        <Label>Images</Label>
                        <Input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageUpload}
                        />
                        {portfolioForm.images.length > 0 && (
                          <p className="text-sm text-emerald-600 mt-1">
                            {portfolioForm.images.length} image(s) selected
                          </p>
                        )}
                      </div>
                      <Button onClick={handleAddPortfolio} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-portfolio-btn">
                        Add to Portfolio
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {portfolios.length === 0 ? (
                  <div className="text-center py-12">
                    <i className="fas fa-images text-5xl text-gray-300 mb-4"></i>
                    <p className="text-gray-500">No portfolio items yet</p>
                    <p className="text-sm text-gray-400">Add your completed projects to attract more clients</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {portfolios.map((project) => (
                      <Card key={project.id} className="overflow-hidden" data-testid={`portfolio-item-${project.id}`}>
                        {project.images?.length > 0 && (
                          <div className="h-40 bg-gray-100">
                            <img src={project.images[0]} alt={project.projectTitle} className="w-full h-full object-cover" />
                          </div>
                        )}
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-semibold">{project.projectTitle}</h4>
                              <Badge variant="outline" className="mt-1">{getServiceLabel(project.serviceType)}</Badge>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => handleDeletePortfolio(project.id)}
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          </div>
                          <p className="text-sm text-gray-500 mt-2">{project.description}</p>
                          <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                            <span><i className="fas fa-map-marker-alt mr-1"></i>{project.location}</span>
                            <span>•</span>
                            <span><i className="fas fa-calendar mr-1"></i>{formatDate(project.completionDate)}</span>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscription Tab */}
          <TabsContent value="subscription">
            <Card>
              <CardHeader>
                <CardTitle>Subscription Management</CardTitle>
                <CardDescription>Manage your contractor subscription</CardDescription>
              </CardHeader>
              <CardContent>
                {subscription?.status === 'active' || subscription?.status === 'trial' ? (
                  <div className="space-y-4">
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
                      <div className="flex items-center gap-4">
                        <div className="p-4 bg-emerald-100 rounded-full">
                          <i className="fas fa-crown text-3xl text-emerald-600"></i>
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-emerald-800">
                            {subscription.status === 'trial' ? 'Free Trial Active' : 'Subscription Active'}
                          </h3>
                          <p className="text-emerald-600">
                            {subscription.status === 'trial' ? 'R0/month (trial)' : 'R150/month'}
                          </p>
                        </div>
                      </div>
                      <div className="mt-4 grid md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">Start Date</p>
                          <p className="font-medium">{formatDate(subscription.startDate)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">End Date</p>
                          <p className="font-medium">{formatDate(subscription.endDate)}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold mb-2">Benefits Included:</h4>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-center gap-2">
                          <i className="fas fa-check text-emerald-600"></i>
                          Profile visible in search results
                        </li>
                        <li className="flex items-center gap-2">
                          <i className="fas fa-check text-emerald-600"></i>
                          Unlimited portfolio uploads
                        </li>
                        <li className="flex items-center gap-2">
                          <i className="fas fa-check text-emerald-600"></i>
                          Direct WhatsApp contact button
                        </li>
                        <li className="flex items-center gap-2">
                          <i className="fas fa-check text-emerald-600"></i>
                          Customer reviews and ratings
                        </li>
                      </ul>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <i className="fas fa-crown text-6xl text-gray-300 mb-4"></i>
                    <h3 className="text-xl font-semibold mb-2">No Active Subscription</h3>
                    <p className="text-gray-500 mb-6">
                      Activate your subscription to appear in search results and receive client inquiries.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4 max-w-xl mx-auto">
                      <Card className="border-2 border-emerald-500">
                        <CardContent className="p-6 text-center">
                          <h4 className="font-semibold text-lg">Free Trial</h4>
                          <p className="text-3xl font-bold text-emerald-600 my-2">R0</p>
                          <p className="text-sm text-gray-500 mb-4">for 1 month</p>
                          <Button 
                            onClick={() => handleStartSubscription(true)}
                            className="w-full bg-emerald-600 hover:bg-emerald-700"
                            data-testid="start-trial-btn"
                          >
                            Start Free Trial
                          </Button>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-6 text-center">
                          <h4 className="font-semibold text-lg">Monthly</h4>
                          <p className="text-3xl font-bold text-gray-800 my-2">R150</p>
                          <p className="text-sm text-gray-500 mb-4">per month</p>
                          <Button 
                            onClick={() => handleStartSubscription(false)}
                            variant="outline"
                            className="w-full"
                            data-testid="start-paid-btn"
                          >
                            Subscribe Now
                          </Button>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <Card>
              <CardHeader>
                <CardTitle>Customer Reviews</CardTitle>
                <CardDescription>
                  {reviews.length} review{reviews.length !== 1 ? 's' : ''} • {contractor.averageRating?.toFixed(1) || '0.0'} average rating
                </CardDescription>
              </CardHeader>
              <CardContent>
                {reviews.length === 0 ? (
                  <div className="text-center py-12">
                    <i className="fas fa-star text-5xl text-gray-300 mb-4"></i>
                    <p className="text-gray-500">No reviews yet</p>
                    <p className="text-sm text-gray-400">Complete projects to receive customer reviews</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reviews.map((review) => (
                      <div key={review.id} className="border-b pb-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold">{review.userName}</p>
                            <div className="flex items-center gap-2">
                              <div className="flex">{renderStars(review.rating)}</div>
                              <span className="text-sm text-gray-500">{formatDate(review.createdAt)}</span>
                            </div>
                          </div>
                        </div>
                        <p className="mt-2 text-gray-600">{review.review}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* View Public Profile */}
        <div className="mt-6 text-center">
          <Button variant="outline" onClick={() => navigate(`/contractor/${contractor.id}`)} data-testid="view-public-profile-btn">
            <i className="fas fa-external-link-alt mr-2"></i> View Public Profile
          </Button>
        </div>
      </div>
    </main>
  );
};

export default ContractorDashboard;
